import { useEffect, useState, useMemo, FC, useCallback, lazy } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import clsx from 'clsx'
import { Helmet } from 'react-helmet'

import { Spin, TabCloseModal, useGlobalState, useDocsContent } from '@pepper/ui'
import DocsTopbar from '@pepper/docs/src/components/Topbar/Topbar'
import DocV2Provider from '@pepper/docs/src/container/EditorSocket/DocV2Provider'
import TryPepperCTA from '@pepper/docs/src/components/Navigation/TryPepperCTA/TryPepperCTA'

import { ReactComponent as SecureFiles } from 'assets/images/undraw_secure_files_re_6vdh.svg'
import AssignmentBriefEditor from 'container/EditDocument/AssignmentBriefEditor/AssignmentBriefEditor'

import AssignmentComponent from './AssignmentData'
import styles from './_.module.css'

const EditorSocket = lazy(
  () => import('@pepper/docs/src/container/EditorSocket/EditorRootWithToken')
)
const EditorOld = lazy(
  () => import('@pepper/docs/src/container/Editor/EditorRoot')
)

interface Params {
  assignmentId: string
}

const PublicDocument: FC = () => {
  const {
    docErrorUpdate,
    editorNetworkState,
    editorUpdateStatus,
    setDocErrorUpdate,
    setEditorUpdateStatus,
    setEditorNetworkState,
  } = useGlobalState()

  const params = useParams<Params>()
  const [socketDataPushed, setSocketDataPushed] = useState<string[]>([])
  const [assignment, setAssignment] = useState<Assignment>()
  const [assignmentError, setAssignmentError] = useState<boolean>(false)
  const [modalVisibility, setModalVisibility] = useState(false)
  const history = useHistory()

  const [showSecure, setShowSecure] = useState(false)

  const docContent = useDocsContent({
    data: {
      id: params.assignmentId,
      apiType: 'PUBLIC_VIEW_PEPPER_DOC',
      excludeContent: true,
    },
  })

  useEffect(() => {
    if (!docErrorUpdate) {
      if (socketDataPushed?.length > 0) {
        setEditorUpdateStatus('UPDATING')
      } else {
        setEditorUpdateStatus('UPDATED')
      }
    }
  }, [socketDataPushed, docErrorUpdate, setEditorUpdateStatus])

  const setUpdatingEditor = useCallback(
    val => {
      val ? setEditorUpdateStatus('UPDATING') : setEditorUpdateStatus('UPDATED')
    },
    [setEditorUpdateStatus]
  )

  const updateSocketCallback = useCallback(
    (data: SocketEventType) => {
      const key = data?.key as string
      if (data?.status === 'error') {
        setDocErrorUpdate(true)
      } else if (data?.status === 'reset') {
        setDocErrorUpdate(false)
        setSocketDataPushed([])
      } else if (data?.status === 'entry' && !docErrorUpdate) {
        setSocketDataPushed(val => {
          return [...val, key]
        })
      } else if (data?.status === 'ok' && !docErrorUpdate) {
        setSocketDataPushed(val => {
          const foundIndex = val?.findIndex(it => it === key)
          if (foundIndex > -1) {
            const updatedValue = [...val]
            updatedValue?.splice(foundIndex, 1)
            return updatedValue
          }
          return val
        })
      }
    },
    [docErrorUpdate, setDocErrorUpdate]
  )

  const accessError = (isError: boolean) => {
    if (isError) {
      setAssignmentError(true)
    }
  }

  const isAuthorized = useMemo(() => {
    if (assignmentError) {
      return false
    }
    return localStorage.getItem('authToken') ? true : false
  }, [assignmentError])

  useEffect(() => {
    if (
      !docContent.isLoading &&
      !docContent.data?.public_access.read &&
      !showSecure
    ) {
      setShowSecure(true)
    }
    if (
      !docContent.isLoading &&
      docContent.data?.public_access.read &&
      showSecure
    ) {
      setShowSecure(false)
    }
  }, [docContent.data?.public_access.read, docContent.isLoading, showSecure])

  const isEditorOld =
    docContent.data &&
    !docContent.data?.is_yjs_enabled &&
    !showSecure &&
    !modalVisibility
  const isEditorSocket =
    docContent.data?.is_yjs_enabled && !showSecure && !modalVisibility
  const isYjsDisabled =
    docContent.data && !docContent.data.is_yjs_enabled && !showSecure
  let body
  if (
    (!isAuthorized && docContent.isLoading) ||
    (isAuthorized && (docContent.isLoading || !assignment))
  ) {
    body = <Spin className={styles.pepperEditorLoader} size="large" />
  } else {
    body = (
      <>
        {docContent.data && (
          <DocV2Provider
            useYjsV2={docContent.data.use_yjs_v2 ?? false}
            docId={docContent.data.pepper_doc_id}
            editorType="PUBLIC_VIEW_PEPPER_DOC"
          >
            <DocsTopbar
              docId={docContent.data.pepper_doc_id}
              docType="PUBLIC_VIEW_PEPPER_DOC"
              useYjsV2={docContent.data.use_yjs_v2 ?? false}
              initialTitle={docContent.data.metadata?.title}
              platform="business"
              rightSlot={
                <>
                  <TryPepperCTA />
                </>
              }
            />
            <div className={styles.peEditableArea}>
              {isEditorSocket && docContent.data?.assignment && (
                <EditorSocket
                  isV2={false}
                  platform="business"
                  useYjsV2={docContent.data.use_yjs_v2 ?? false}
                  assignmentId={params.assignmentId}
                  pepperDocId={docContent.data.pepper_doc_id}
                  companyId={docContent.data.project.company.id}
                  editorToken={docContent.data['x-editor-token']}
                  stopUpdateForcefully={
                    !(
                      Boolean(docContent.data.public_access.comment) ||
                      Boolean(docContent.data.public_access.edit)
                    )
                  }
                  isEditable={Boolean(docContent.data.public_access.edit)}
                  assignmentData={assignment}
                  assignmentCode={docContent.data.assignment.assignment_code}
                  netWorkState={editorNetworkState}
                  setNetworkStatus={setEditorNetworkState}
                  publicAccess={{
                    read: Boolean(docContent.data.public_access.read),
                    comment: Boolean(docContent.data.public_access.comment),
                    edit: Boolean(docContent.data.public_access.edit),
                  }}
                  isAuthorized={isAuthorized}
                  publicAssignmentTitle={docContent.data.assignment.title}
                  editorType="PUBLIC_VIEW_PEPPER_DOC"
                  updateSocketCallback={updateSocketCallback}
                  AssignmentBriefEditor={AssignmentBriefEditor}
                />
              )}
              {isEditorOld && docContent.data?.assignment && (
                <EditorOld
                  platform="business"
                  assignmentId={params.assignmentId}
                  pepperDocId={docContent.data.pepper_doc_id}
                  companyId={docContent.data.project.company.id}
                  editorToken={docContent.data['x-editor-token']}
                  stopUpdateForcefully={
                    !(
                      Boolean(docContent.data.public_access.comment) ||
                      Boolean(docContent.data.public_access.edit)
                    )
                  }
                  isEditable={Boolean(docContent.data.public_access.edit)}
                  updatingEditor={editorUpdateStatus === 'UPDATING'}
                  setUpdatingEditor={setUpdatingEditor}
                  assignmentData={assignment}
                  assignmentCode={docContent.data.assignment.assignment_code}
                  netWorkState={editorNetworkState}
                  setNetworkStatus={setEditorNetworkState}
                  publicAccess={{
                    read: Boolean(docContent.data.public_access.read),
                    comment: Boolean(docContent.data.public_access.comment),
                    edit: Boolean(docContent.data.public_access.edit),
                  }}
                  shouldClearTimeout={false}
                  setShouldClearTimeout={() => null}
                  isAuthorized={isAuthorized}
                  publicAssignmentTitle={docContent.data.assignment.title}
                  setErrorUpdate={setDocErrorUpdate}
                  editorType="PUBLIC_VIEW_PEPPER_DOC"
                  AssignmentBriefEditor={AssignmentBriefEditor}
                />
              )}
              {showSecure && (
                <div className={styles.noAccessWrapper}>
                  <SecureFiles className={styles.secureImage} />
                  <p>
                    Oh no! It seems you don&apos;t have access to this file! :(
                  </p>
                </div>
              )}
            </div>
          </DocV2Provider>
        )}
      </>
    )
  }

  return (
    <div className={clsx(styles.pepperEditorWrap, styles.customScrollBar)}>
      <Helmet>
        <title>Doc | {assignment?.assignment_title ?? ''}</title>
      </Helmet>
      {isYjsDisabled && (
        <TabCloseModal
          modalVisibility={modalVisibility}
          setModalVisibility={setModalVisibility}
          id={params.assignmentId}
          returnHome={() => history.push('/')}
        />
      )}
      {isAuthorized && !modalVisibility && (
        <AssignmentComponent
          assignmentId={params.assignmentId}
          updateData={setAssignment}
          updateError={accessError}
        />
      )}
      {body}
    </div>
  )
}
export default PublicDocument
